import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue';
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, renderSlot as _renderSlot, unref as _unref, withCtx as _withCtx, createSlots as _createSlots, createBlock as _createBlock } from "vue";
import { useColors } from "@/common/composables/colors";
import { pxToRem } from "@/common/helpers/style";
import { computed, onBeforeMount, onBeforeUpdate, ref, useSlots } from "vue";
// Components
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
// Setup
export default /*@__PURE__*/ _defineComponent({
    __name: 'RecAccordion',
    props: {
        modelValue: { default: undefined },
        multiple: { type: Boolean },
        color: { default: "primary" },
        backgroundColor: { default: undefined },
        borderColor: { default: undefined },
        textColor: { default: "desat" },
        contentBackgroundColor: { default: "white" },
        contentPadding: { default: 20 },
        headerPadding: { default: () => [12, 20] },
        contained: { type: Boolean },
        extendedTabHeaders: { default: () => [] }
    },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        _useCssVars(_ctx => ({
            "v253d56ae": (borderColorCss.value),
            "v223036a0": (backgroundColorCss.value),
            "v216bb83c": (colorCss.value),
            "v4c6c3778": (headerPaddingRem.value),
            "v37db5f2f": (textColorCss.value),
            "v0417ef45": (contentBackgroundColorCss.value),
            "v12d363c0": (contentPaddingRem.value)
        }));
        /**
         * RecAccordion.vue
         *
         * Props
         *   modelValue (optional)
         *     used to create a bidirectional binding to active tab's index.
         *
         *   color (defaults to "primary")
         *     used to pass a valid base colour name (primary, secondary, dest or any base Material Design colour) to set tabs
         *     colour. bg-<color>-lighten-5 class will be applied as tabs background colour while text-<color> class will be
         *     applied as header's text color.
         *     !!!Invalid colour names will throw an error!!!
         *
         *   textColor (defaults to "desat")
         *     used to pass a valid base colour name (primary, secondary, dest or any base Material Design colour). text-<color>
         *     class will be applied as tabs content's text color.
         *     !!!Invalid colour names will throw an error!!!
         *
         *   multiple (defaults to "false")
         *     used to allow multiple accordion tabs to be opened at the same time.
         *
         *   contentPadding (defaults to 20)
         *      used to set a custom padding in the tabs content.
         *
         *   headerPadding (defaults to [12, 20])
         *      used to set a custom padding in the tabs headers.
         *
         *   contained (defaults to false)
         *      used to make the accordion confined in available height and make active tab's content scrollable.
         *
         * Emits
         *   update:modelValue
         *     emitted when the accordion's active tab is being changed. Sends the index of the new active
         *     tab OR undefined if all tabs are closed.
         *
         *
         * Slots
         *   In order to generate tabs inside a RecAccordion component, use <template #tab-(number)-header></template> and
         *   <template #tab-(number)-content></template> to access dynamically generated slots. Slots content can be a string,
         *   an HTML element or a Vue component (or multiple HTML els or Vue components).
         *   !!!Invalid slot name will throw an error!!!
         *
         *   E.g.1 To access 1st tab's header, use <template #tab-1-header>...</template> and put header's content inside.
         *
         *   E.g.2 To access 1st tab's content, use <template #tab-1-content>...</template> and put tab's content inside.
         *
         *
         * Customization
         *   - Prefer passing PrimeFlex utility classes through the pt attribute to the target element over using component's
         *     style block. Pt attributes can be used to apply CSS classes and/or apply style rules directly on an element
         *     (see docs at https://primeflex.org/ and https://primevue.org/accordion/#pt.image)
         *
         *   - If usage of the component's style block is necessary, all element nested inside a PrimeVue component can be
         *     accessed using Vue's :deep selector.
         *
         *
         * Example of usage
         *
         * <RecAccordion
         *   v-model="accordionTab"
         *   :multiple="accordionMultiple"
         *   color="ai-primary"
         *   text-color="ai-primary-desat"
         * >
         *   <template #tab-1-header>TAB 1</template>
         *   <template #tab-1-content>
         *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et
         *     dolore magna aliqua. Placerat in egestas erat imperdiet sed euismod nisi. Dolor sit amet consectetur
         *     adipiscing elit ut aliquam. Amet volutpat consequat mauris nunc congue nisi vitae. Integer malesuada nunc
         *     vel risus commodo viverra. Velit euismod in pellentesque massa placerat duis. Risus pretium quam vulputate
         *     dignissim. Turpis in eu mi bibendum neque. Auctor elit sed vulputate mi sit amet mauris commodo quis.
         *     Velit sed ullamcorper morbi tincidunt ornare massa eget egestas. Risus at ultrices mi tempus imperdiet
         *     nulla. Id donec ultrices tincidunt arcu non. Ac tortor dignissim convallis aenean et tortor.
         *   </template>
         *
         *
         *   <!-- ----------------------------------------------------------------------- -->
         *   <!-- This tab will be rendered only if accordionMultiple ref's value is true -->
         *   <!-- ----------------------------------------------------------------------- -->
         *   <template v-if="accordionMultiple" #tab-2-header>TAB 2</template>
         *   <template v-if="accordionMultiple" #tab-2-content>
         *     <div class="mb-5">Some content</div>
         *     <Button class="bg-primary" label="PrimeVue primary" />
         *     <Button severity="warning" class="ml-2" label="PrimeVue warning" />
         *     <Button severity="danger" class="ml-2" label="PrimeVue danger" />
         *   </template>
         *   <!-- ----------------------------------------------------------------------- -->
         *   <!-- ----------------------------------------------------------------------- -->
         *   <!-- ----------------------------------------------------------------------- -->
         *
         *   <template #tab-3-header>TAB 3</template>
         *   <template #tab-3-content>
         *     <Button class="bg-primary" label="PrimeVue Button" />
         *   </template>
         * </RecAccordion>
         *
         *
         * Passing elements inside the header slot
         *
         *   It may be necessary to add controls to an accordion tab's header. Keep in mind that PrimeVue gives you access to
         *   the embedded header link used to toggle the tab's state. Anything added there will be rendered inside the header's
         *   existing <a> element. In order to make the custom controls usable, it might be necessary to stop propagation of
         *   some specific events such as "click" or "keydown".
         *   Here's an example:
         *
         * <RecAccordion v-model="accordionTab">
         *   <template #tab-1-header>
         *     <span>Some text</span>
         *
         *     <!-- Stop propagation of click event and enter key press event. -->
         *     <DropdownMenu class="ml-auto" @click.stop @keydown.enter.stop>
         *       <template #button>...Button label...</template>
         *       <template #menu>...List of items...</template>
         *     </DropdownMenu>
         *   </template>
         *   ...
         * </RecAccordion>
         *
         */
        const props = __props;
        const emit = __emit;
        // Using a WritableComputedRef allows replacing PrimeVue Accordion v-model:active-index "null" value with "undefined"
        // when assigning a value to the modelValue prop.
        const model = computed({
            get: () => props.modelValue,
            set: (val) => {
                const value = val === null ? undefined : val;
                emit("update:modelValue", value);
            }
        }); // TODO: use defineModel instead
        // Tabs logic
        const tabs = ref([]);
        onBeforeUpdate(() => {
            setTabs(useSlots());
        });
        const getIndex = (slotName) => {
            const slotPrefix = slotName.match(/^tab-\d+/)?.[0];
            const target = slotName
                .match(/-(header|content|headericon)$/)?.[0]
                ?.replace("-", "")
                .replace("content", "default");
            if (!slotPrefix || !target) {
                throw new Error(`Invalid slot name ${slotName} was passed to RecAccordion.`);
            }
            const index = Number(slotPrefix.replace(/[^0-9]+/g, ""));
            return { index, target };
        };
        const setTabs = (slots) => {
            tabs.value = Object.keys(slots)
                .reduce((acc, slotName) => {
                const { index, target } = getIndex(slotName);
                const current = acc[index] ?? {};
                acc[index] = {
                    ...current,
                    [target.replace("content", "default")]: slotName
                };
                return acc;
            }, [])
                .filter((tab) => !!tab);
        };
        setTabs(useSlots());
        // Colours logic
        const { isValidMDBaseColor } = useColors();
        onBeforeMount(async () => {
            setTabs(useSlots());
            // "color" & "textColor" props validation
            const colorPropIsValid = await isValidMDBaseColor(props.color);
            const textColorPropIsValid = await isValidMDBaseColor(props.textColor);
            if (!colorPropIsValid) {
                console.warn(`"${props.color}" passed to RecAccordion's "color" prop is not a valid MD color`);
            }
            if (!textColorPropIsValid) {
                console.warn(`"${props.color}" passed to RecAccordion's "textColor" prop is not a valid MD color`);
            }
        });
        // Customization logic
        const pt = computed(() => ({
            root: {
                class: ["rec-accordion", { "rec-accordion--contained": props.contained }]
            },
            accordiontab: {
                root: ({ context }) => ({
                    id: context ? `tab-${context.index + 1}` : undefined,
                    class: [
                        "rec-accordion-tab",
                        {
                            "rec-accordion-tab--active": context?.active,
                            "rec-accordion-tab--has-header-extension": context
                                ? props.extendedTabHeaders.includes(context.index + 1)
                                : false
                        }
                    ]
                }),
                header: ({ context }) => ({
                    id: context ? `tab-${context.index + 1}-header` : undefined,
                    class: "rec-accordion-tab__header"
                }),
                headerAction: "rec-accordion-tab__header-action",
                headerIcon: "rec-accordion-tab__header-icon",
                headerTitle: "rec-accordion-tab__header-title",
                content: "rec-accordion-tab__content"
            }
        }));
        const processColorName = (color) => {
            if (color.startsWith("var(--"))
                return color;
            return `var(--color-${color})`;
        };
        const colorCss = computed(() => processColorName(props.color));
        const textColorCss = computed(() => processColorName(props.textColor));
        const borderColorCss = computed(() => processColorName(props.borderColor ?? `${props.color}-lighten-3`));
        const backgroundColorCss = computed(() => processColorName(props.backgroundColor ?? `${props.color}-lighten-5`));
        const contentBackgroundColorCss = computed(() => processColorName(props.contentBackgroundColor));
        const contentPaddingRem = computed(() => paddingPropProcessing(props.contentPadding));
        const headerPaddingRem = computed(() => paddingPropProcessing(props.headerPadding));
        // ...helper fct
        const paddingPropProcessing = (val) => {
            if (typeof val === "string") {
                return val.split(" ").map(pxToRem).join(" ");
            }
            if (Array.isArray(val)) {
                return val.map(pxToRem).join(" ");
            }
            return pxToRem(val);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Accordion), {
                "active-index": model.value,
                "onUpdate:activeIndex": _cache[0] || (_cache[0] = ($event) => ((model).value = $event)),
                pt: pt.value,
                multiple: __props.multiple,
                unstyled: ""
            }, {
                default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tabSlots, index) => {
                        return (_openBlock(), _createBlock(_unref(AccordionTab), {
                            key: `tab-${index}`
                        }, _createSlots({ _: 2 }, [
                            _renderList(tabSlots, (slot, slotName) => {
                                return {
                                    name: slotName,
                                    fn: _withCtx(() => [
                                        _renderSlot(_ctx.$slots, slot)
                                    ])
                                };
                            })
                        ]), 1024));
                    }), 128))
                ]),
                _: 3
            }, 8, ["active-index", "pt", "multiple"]));
        };
    }
});
